// Library — the whole archive in one place (08_AUTHORING_APP.md). Lists
// every draft, published or not, with a status filter; click one to open
// it in Compose. The day-to-day editing surface is Compose's sidebar;
// Library is the "see everything, jump to anything" view.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useNavStore } from "../../store/navStore";
import { useComposeStore } from "../../store/composeStore";
import { listDrafts, type Draft } from "../../lib/storage/drafts";
import { wordCount } from "../../lib/post";

type Filter = "all" | "published" | "drafts";

export function LibraryTab() {
  const user = useAuthStore((s) => s.user)!;
  const setTab = useNavStore((s) => s.setTab);
  const openDraft = useComposeStore((s) => s.openDraft);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setDrafts(await listDrafts(user.sub));
    setLoading(false);
  }, [user.sub]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => drafts.filter((d) =>
    filter === "all" ? true : filter === "published" ? !!d.published_slug : !d.published_slug,
  ), [drafts, filter]);

  const open = (id: string) => { openDraft(id); setTab("compose"); };

  const counts = {
    all: drafts.length,
    published: drafts.filter((d) => d.published_slug).length,
    drafts: drafts.filter((d) => !d.published_slug).length,
  };

  return (
    <div className="lib-root">
      <div className="lib-col">
        <div className="lib-head">
          <h1 className="lib-title">Library</h1>
          <div className="lib-filters">
            {(["all", "published", "drafts"] as Filter[]).map((f) => (
              <button key={f} className={"lib-filter" + (filter === f ? " lib-filter-on" : "")} onClick={() => setFilter(f)}>
                {f} <span className="lib-count">{counts[f]}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? <div className="lib-muted">loading…</div>
          : shown.length === 0 ? <div className="lib-muted">Nothing here yet. Start writing in Compose.</div>
          : <ul className="lib-list">
              {shown.map((d) => (
                <li key={d.id} className="lib-item" onClick={() => open(d.id)}>
                  <div className="lib-item-main">
                    <span className="lib-item-title">{d.title.trim() || "Untitled"}</span>
                    <span className="lib-item-sub">
                      {d.summary.trim() || `${wordCount(d.body)} words`}
                    </span>
                  </div>
                  <span className={"lib-status " + (d.published_slug ? "lib-status-pub" : "")}>
                    {d.published_slug ? "published" : "draft"}
                  </span>
                  <span className="lib-date">{new Date(d.updated_at).toISOString().slice(0, 10)}</span>
                </li>
              ))}
            </ul>}
      </div>

      <style>{`
        .lib-root { height: 100%; overflow: auto; display: flex; justify-content: center; padding: var(--space-6) var(--space-5); }
        .lib-col { width: min(760px, 100%); }
        .lib-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-5); gap: var(--space-4); flex-wrap: wrap; }
        .lib-title { font-family: var(--font-serif); font-size: 30px; font-weight: 600; margin: 0; color: var(--ink); }
        .lib-filters { display: flex; gap: var(--space-1); }
        .lib-filter { font-family: var(--font-sans); font-size: 12px; color: var(--ink-soft); background: transparent; border: 1px solid var(--line); border-radius: 999px; padding: 5px 12px; cursor: pointer; text-transform: capitalize; }
        .lib-filter-on { color: var(--teal); border-color: var(--teal); background: var(--teal-dim); }
        .lib-count { font-family: var(--font-mono); font-size: 10px; opacity: 0.7; }
        .lib-muted { color: var(--ink-faint); font-size: 14px; }
        .lib-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .lib-item { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-3) var(--space-2); border-bottom: 1px solid var(--line); cursor: pointer; border-radius: var(--radius-1); }
        .lib-item:hover { background: var(--paper-raised); }
        .lib-item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .lib-item-title { font-size: 16px; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .lib-item-sub { font-size: 12px; color: var(--ink-faint); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .lib-status { font-family: var(--font-mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-faint); padding: 3px 8px; border-radius: 999px; background: var(--paper-sunk); }
        .lib-status-pub { color: var(--teal); background: var(--teal-dim); }
        .lib-date { font-family: var(--font-mono); font-size: 11px; color: var(--ink-faint); flex: none; }
      `}</style>
    </div>
  );
}
